import React, { useEffect, useState, Fragment, Suspense, lazy, memo, createElement } from 'react';
import { Check, X, ShieldCheck, Star, ChevronDown, ChevronLeft, ChevronRight, Clock, Zap, BookOpen, Map as MapIcon, ClipboardCheck, Calendar, FileQuestion, Layers, Gift, Stethoscope, Download, Lock, Globe, Brain, ArrowRight, Sparkles, AlertCircle } from 'lucide-react';
const CommentsSection = lazy(() => import('./components/CommentsSection').then(m => ({
  default: m.CommentsSection
})));
const SocialProofPopup = lazy(() => import('./components/SocialProofPopup').then(m => ({
  default: m.SocialProofPopup
})));
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/q5HAAqdLaaTY3VDjsCmoRA/ChatGPT_Image_26_may_2026,_22_02_07.webp';
const CHECKOUT_URL = 'https://pay.hotmart.com/V106016292C?checkoutMode=10';
const PrimaryCTA = ({
  children,
  href,
  scrollTo,
  size = 'lg',
  full = true
}: {
  children: React.ReactNode;
  href?: string;
  scrollTo?: string;
  size?: 'md' | 'lg';
  full?: boolean;
}) => {
  const sizeClasses = size === 'lg' ? 'px-7 py-4 text-base md:text-lg' : 'px-6 py-3 text-sm md:text-base';
  const widthClass = full ? 'w-full' : 'w-auto';
  const baseClass = `btn-primary inline-flex items-center justify-center gap-2 rounded-xl uppercase tracking-wider ${sizeClasses} ${widthClass}`;
  if (href) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className={`${baseClass} no-underline`} data-id="element-0">
        {children}
        <ArrowRight className="w-5 h-5 shrink-0" data-id="element-1" />
      </a>;
  }
  return <button onClick={() => {
    if (scrollTo) {
      const el = document.getElementById(scrollTo);
      if (el) el.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }} className={baseClass} data-id="element-2">
      {children}
      <ArrowRight className="w-5 h-5 shrink-0" data-id="element-3" />
    </button>;
};
const SectionHeader = ({
  eyebrow,
  title,
  subtitle,
  light = false
}: {
  eyebrow?: string;
  title: React.ReactNode;
  subtitle?: string;
  light?: boolean;
}) => <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14" data-id="element-4">
    {eyebrow && <p className={`text-xs md:text-sm font-bold uppercase tracking-[0.2em] mb-3 ${light ? 'text-cyan-400' : 'text-cyan-600'}`} data-id="element-5">
        {eyebrow}
      </p>}
    <h2 className={`text-3xl md:text-5xl font-bold leading-[1.1] mb-4 ${light ? 'text-white' : 'text-navy-900'}`} data-id="element-6">
      {title}
    </h2>
    {subtitle && <p className={`text-base md:text-lg leading-relaxed ${light ? 'text-navy-100' : 'text-ink-500'}`} data-id="element-7">
        {subtitle}
      </p>}
  </div>;
export function App() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59
  });
  useEffect(() => {
    let endTimeStr = localStorage.getItem('offerEndTime');
    let endTime = endTimeStr ? parseInt(endTimeStr, 10) : 0;
    if (!endTime || endTime <= Date.now()) {
      endTime = Date.now() + 24 * 60 * 60 * 1000;
      localStorage.setItem('offerEndTime', String(endTime));
    }
    const tick = () => {
      const diff = Math.max(0, endTime - Date.now());
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
      const seconds = Math.floor(diff % (1000 * 60) / 1000);
      setTimeLeft({
        hours,
        minutes,
        seconds
      });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);
  const carouselSlides = [{
    title: 'División del Esqueleto · Axial y Apendicular',
    subtitle: 'Sistema Esquelético · P.23',
    image: 'https://cdn.magicpatterns.com/uploads/kgztbYirAnPspJm9AH3Q3w/1_anatomia.webp'
  }, {
    title: 'Huesos del Tronco · Costillas y Columna',
    subtitle: 'Sistema Esquelético · P.42',
    image: 'https://cdn.magicpatterns.com/uploads/dqCCnkCiSqb11FfqAZyFu4/2_anatomia.webp'
  }, {
    title: 'Principales Músculos del Cuerpo Humano',
    subtitle: 'Sistema Muscular · P.70',
    image: 'https://cdn.magicpatterns.com/uploads/kTmbUJa9PiDff6hPwJEP9s/3_anatomia.webp'
  }, {
    title: 'Anatomía del Corazón · Cámaras y Vasos',
    subtitle: 'Sistema Cardiovascular · P.86',
    image: 'https://cdn.magicpatterns.com/uploads/p33T7mHPHrSiDoPNaqrgPT/4_anatomia.webp'
  }, {
    title: 'Órganos del Sistema Respiratorio',
    subtitle: 'Sistema Respiratorio · P.112',
    image: 'https://cdn.magicpatterns.com/uploads/kqoMrKSyiHEnkzpiQ6KDwT/5_anatomia.webp'
  }, {
    title: 'Sistema Reproductor Femenino',
    subtitle: 'Aparato Reproductor · P.138',
    image: 'https://cdn.magicpatterns.com/uploads/4iF4HqngxPSreHtF3y5tbX/6_anatomia.webp'
  }];
  const nextSlide = () => setCurrentSlide(p => (p + 1) % carouselSlides.length);
  const prevSlide = () => setCurrentSlide(p => (p - 1 + carouselSlides.length) % carouselSlides.length);
  useEffect(() => {
    const loadPixel = () => {
      if ((window as any).fbq) return;
      const f = window as any;
      const n: any = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const t = document.createElement('script');
      t.async = true;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(t);
      n('init', '846575091369965');
      n('track', 'PageView');
    };
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(loadPixel, {
        timeout: 3000
      });
    } else {
      setTimeout(loadPixel, 2500);
    }
  }, []);
  useEffect(() => {
    if (document.querySelector('script[src="https://cdn.utmify.com.br/scripts/utms/latest.js"]')) return;
    const s = document.createElement('script');
    s.src = 'https://cdn.utmify.com.br/scripts/utms/latest.js';
    s.async = true;
    s.defer = true;
    s.setAttribute('data-utmify-prevent-subids', '');
    document.head.appendChild(s);
  }, []);
  useEffect(() => {
    if (document.querySelector('script[src="https://cdn.utmify.com.br/scripts/pixel/pixel.js"]')) return;
    (window as any).pixelId = '6a16598d4416905c6a37ac19';
    const a = document.createElement('script');
    a.setAttribute('async', '');
    a.setAttribute('defer', '');
    a.setAttribute('src', 'https://cdn.utmify.com.br/scripts/pixel/pixel.js');
    document.head.appendChild(a);
  }, []);
  const bonuses = [{
    icon: BookOpen,
    tag: 'GUÍA PRINCIPAL',
    title: 'Guía Principal · 192 páginas',
    desc: 'Los 12 sistemas del cuerpo humano explicados con ilustraciones detalladas y conexiones claras entre sistemas.',
    value: 27,
    featured: true
  }, {
    icon: MapIcon,
    tag: 'BONO #1',
    title: 'Mapa Maestro de Anatomía',
    desc: 'Visión integrada de los 12 sistemas y cómo se conectan entre sí. El panorama completo antes del detalle.',
    value: 12
  }, {
    icon: BookOpen,
    tag: 'BONO #2',
    title: 'Guía Express de Términos',
    desc: 'Vocabulario, prefijos, sufijos, planos y términos de dirección — todo lo que se evalúa en exámenes.',
    value: 12
  }, {
    icon: ClipboardCheck,
    tag: 'BONO #3',
    title: 'Checklist de Estudio',
    desc: '13 checklists con 336 puntos de verificación. Nunca más llegues al examen con dudas.',
    value: 12
  }, {
    icon: Calendar,
    tag: 'BONO #4',
    title: 'Plan de 14 Días',
    desc: 'Tu ruta de estudio día por día con sesiones de 45-60 min y repaso espaciado incorporado.',
    value: 12
  }, {
    icon: FileQuestion,
    tag: 'BONO #5',
    title: 'Cuaderno de Autoevaluación',
    desc: '291 preguntas con respuestas detalladas organizadas por sistema. Simulacro de examen incluido.',
    value: 12
  }, {
    icon: Zap,
    tag: 'BONO #6',
    title: 'Fichas de Memorización',
    desc: '25 fichas visuales para el repaso de último minuto. Una ficha de 60 segundos por sistema.',
    value: 12
  }];
  const benefits = [{
    before: 'Estudiás sin orden ni método claro',
    after: 'Tenés un plan de 14 días listo para usar'
  }, {
    before: 'No sabés qué repasar antes del examen',
    after: 'Fichas de memorización rápida para los últimos días'
  }, {
    before: 'Memorizás y olvidás todo al día siguiente',
    after: 'Detectás vacíos con 291 preguntas de autoevaluación'
  }, {
    before: 'Los sistemas parecen piezas sueltas',
    after: 'Entendés el cuerpo como un sistema integrado'
  }, {
    before: 'Improvisás cada semana de estudio',
    after: 'Abrís el PDF y estudiás sin estrés'
  }, {
    before: 'Llegás al examen con esperanza',
    after: 'Llegás al examen con confianza real'
  }];
  const idealFor = ['Estudiás medicina, enfermería, fisioterapia, kinesiología, nutrición u odontología', 'Querés estudiar anatomía de forma visual y ordenada', 'Necesitás organizarte mejor sin perder tiempo en planificar', 'Tenés exámenes próximos y querés llegar con seguridad', 'Querés una base sólida de anatomía para toda la carrera', 'Buscás una herramienta práctica, no otro libro de 800 páginas'];
  const testimonials = [{
    quote: 'Pasé de estudiar sin orden a sentir que por fin entendía la lógica del cuerpo humano. Las ilustraciones cambiaron todo.',
    name: 'María G.',
    role: 'Estudiante de Enfermería · Buenos Aires'
  }, {
    quote: 'Las fichas y el plan de 14 días me ayudaron muchísimo antes del parcial. Reduje mi tiempo de estudio un 80%.',
    name: 'Lucas T.',
    role: 'Estudiante de Medicina · Córdoba'
  }, {
    quote: 'La autoevaluación me mostró exactamente qué temas me faltaban. Aprobé anatomía con 9 después de venir arrastrando la materia.',
    name: 'Valentina R.',
    role: 'Estudiante de Fisioterapia · Bogotá'
  }];
  const faqs = [{
    q: '¿Todo está en PDF?',
    a: 'Sí, el 100% del Mega Pack es en formato PDF. Lo abrís, descargás o imprimís en cualquier dispositivo.'
  }, {
    q: '¿Cuándo recibo el material?',
    a: 'Inmediatamente después de confirmar el pago recibís un correo con el acceso completo. Sin esperas.'
  }, {
    q: '¿Sirve para mi carrera?',
    a: 'Sí. Está diseñado para estudiantes de medicina, enfermería, fisioterapia, kinesiología, nutrición y odontología.'
  }, {
    q: '¿Puedo verlo desde el celular?',
    a: 'Sí, lo podés usar en celular, tablet o computadora. También lo podés imprimir si preferís estudiar en papel.'
  }, {
    q: '¿Necesito conocimientos previos?',
    a: 'No. Funciona tanto si arrancás desde cero como si ya tenés conocimientos y querés organizarlos mejor.'
  }, {
    q: '¿Qué pasa si no me gusta?',
    a: 'Tenés 7 días para pedir reembolso completo. Sin preguntas, sin trámites. El riesgo es 100% nuestro.'
  }, {
    q: '¿Cómo recibo los bonos?',
    a: 'Todos los bonos llegan junto con la guía principal en el mismo correo de acceso. Acceso inmediato a todo el sistema.'
  }];
  const TestimonialCard = ({
    quote,
    name,
    role
  }: {
    quote: string;
    name: string;
    role: string;
  }) => <div className="card-premium p-6 md:p-7 h-full flex flex-col" data-id="element-8">
      <div className="flex items-center gap-1 text-amber-400 mb-3" data-id="element-9">
        {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-10" />)}
      </div>
      <p className="text-ink-700 leading-relaxed mb-5 flex-1 text-[15px]" data-id="element-11">
        "{quote}"
      </p>
      <div className="pt-4 border-t border-navy-100" data-id="element-12">
        <p className="text-navy-900 font-bold text-sm" data-id="element-13">{name}</p>
        <p className="text-ink-400 text-xs mt-0.5" data-id="element-14">{role}</p>
      </div>
    </div>;
  return <div className="min-h-screen bg-white text-ink-900 selection:bg-cyan-500/30 selection:text-navy-900 overflow-x-hidden" data-id="element-15">
      {/* Urgency banner */}
      <div className="w-full bg-red-600 py-2.5 px-4 flex justify-center items-center" data-id="element-16">
        <div className="flex items-center gap-2.5 max-w-4xl mx-auto text-center" data-id="element-17">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse-fast shrink-0 shadow-[0_0_8px_rgba(255,255,255,0.8)]" data-id="element-18"></span>
          <p className="text-[11px] md:text-sm font-bold tracking-wider text-white uppercase" data-id="element-19">
            Oferta de lanzamiento · Solo primeros 200 estudiantes · USD 9.90
          </p>
        </div>
      </div>

      {/* ───── HERO ───── */}
      <section className="hero-bg relative overflow-hidden" data-id="element-20">
        <div className="absolute inset-0 hero-grid opacity-50 pointer-events-none" data-id="element-21"></div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-16 pb-16 md:pb-20 text-center" data-id="element-22">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-6" data-id="element-23">
            <Download className="w-3.5 h-3.5 text-cyan-400" data-id="element-24" />
            <p className="text-[11px] md:text-xs font-bold tracking-wider text-white uppercase" data-id="element-25">
              100% PDF · Descarga inmediata
            </p>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] mb-5" data-id="element-26">
            Domina <span className="cyan-gradient-text" data-id="element-27">Anatomía Humana</span>{' '}
            sin estudiar a ciegas
          </h1>

          <p className="text-base md:text-lg text-navy-100 leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-28">
            La guía ilustrada para estudiantes que necesitan entender los{' '}
            <span className="text-white font-semibold" data-id="element-29">
              12 sistemas del cuerpo humano
            </span>
            , ahorrar tiempo y llegar al examen con más seguridad.
          </p>

          {/* Hero mockup */}
          <div className="relative max-w-2xl mx-auto mb-10" data-id="element-30">
            <div className="absolute inset-0 bg-cyan-400/20 blur-[100px] rounded-full pointer-events-none" data-id="element-31"></div>
            <img src={HERO_IMAGE} alt="Mega Pack · Anatomía Humana — 12 sistemas del cuerpo" width={1080} height={1080} fetchPriority="high" decoding="sync" loading="eager" className="relative z-10 w-full h-auto object-contain" data-id="element-32" />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-6 max-w-xl mx-auto" data-id="element-33">
            <PrimaryCTA scrollTo="oferta" full data-id="element-34">
              Quiero mi Mega Pack ahora
            </PrimaryCTA>
            <button onClick={() => {
            const el = document.getElementById('incluye');
            if (el) el.scrollIntoView({
              behavior: 'smooth'
            });
          }} className="btn-secondary inline-flex items-center justify-center gap-2 px-7 py-4 text-base rounded-xl" data-id="element-35">
              Ver todo lo que incluye
            </button>
          </div>

          <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center text-xs md:text-sm text-navy-100" data-id="element-36">
            <span className="flex items-center gap-1.5" data-id="element-37">
              <Zap className="w-4 h-4 text-cyan-400" data-id="element-38" />
              Acceso inmediato
            </span>
            <span className="flex items-center gap-1.5" data-id="element-39">
              <Lock className="w-4 h-4 text-cyan-400" data-id="element-40" />
              Pago único
            </span>
            <span className="flex items-center gap-1.5" data-id="element-41">
              <ShieldCheck className="w-4 h-4 text-cyan-400" data-id="element-42" />
              Garantía 7 días
            </span>
          </div>

          {/* Mini bullets */}
          <div className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-id="element-43">
            {[{
            icon: BookOpen,
            label: '192 páginas ilustradas'
          }, {
            icon: Layers,
            label: '12 sistemas del cuerpo'
          }, {
            icon: Gift,
            label: '6 bonos estratégicos'
          }, {
            icon: Stethoscope,
            label: 'Para estudiantes de salud'
          }].map((item, i) => <div key={i} className="flex items-center gap-3 p-3 md:p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-44">
                <div className="w-9 h-9 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center shrink-0" data-id="element-45">
                  <item.icon className="w-4 h-4 text-cyan-400" data-id="element-46" />
                </div>
                <p className="text-xs md:text-sm font-semibold text-white leading-tight text-left" data-id="element-47">
                  {item.label}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* ───── PROBLEM ───── */}
      <section className="py-16 md:py-24 bg-white" data-id="element-48">
        <div className="max-w-4xl mx-auto px-4" data-id="element-49">
          <SectionHeader title={<>
                Si estudiar anatomía te abruma,
                <br className="hidden md:block" data-id="element-51" /> no sos el único
              </>} data-id="element-50" />

          <div className="grid md:grid-cols-2 gap-4 md:gap-5 mb-10" data-id="element-52">
            {['Pasás horas estudiando y sentís que olvidás todo', 'No sabés por dónde empezar ni qué repasar primero', 'Los términos anatómicos te confunden', 'Los sistemas del cuerpo parecen desconectados', 'Llega el examen y sentís inseguridad', 'Los libros tradicionales son pesados y poco prácticos'].map((item, i) => <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-mist border border-navy-100" data-id="element-53">
                <div className="mt-0.5 w-6 h-6 rounded-full bg-red-50 border border-red-200 flex items-center justify-center shrink-0" data-id="element-54">
                  <X className="w-3.5 h-3.5 text-red-500" strokeWidth={3} data-id="element-55" />
                </div>
                <p className="text-ink-700 leading-relaxed text-sm md:text-base" data-id="element-56">
                  {item}
                </p>
              </div>)}
          </div>

          <div className="text-center max-w-2xl mx-auto" data-id="element-57">
            <p className="text-xl md:text-2xl font-bold text-navy-900 leading-snug" data-id="element-58">
              No necesitás estudiar más horas.{' '}
              <span className="cyan-gradient-text" data-id="element-59">
                Necesitás un sistema mejor.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* ───── SOLUTION ───── */}
      <section className="py-16 md:py-24 bg-mist" data-id="element-60">
        <div className="max-w-6xl mx-auto px-4" data-id="element-61">
          <SectionHeader title="Eso es exactamente lo que resuelve el Mega Pack" subtitle="Un sistema completo, visual y listo para usar — sin teoría vacía, sin libros imposibles, sin suscripciones." data-id="element-62" />

          <div className="max-w-3xl mx-auto" data-id="element-63">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-64">
              {[{
              icon: BookOpen,
              label: 'Guía principal ilustrada'
            }, {
              icon: MapIcon,
              label: 'Ruta de estudio clara'
            }, {
              icon: Brain,
              label: 'Términos simplificados'
            }, {
              icon: ClipboardCheck,
              label: 'Checklists de repaso'
            }, {
              icon: FileQuestion,
              label: 'Autoevaluación real'
            }, {
              icon: Zap,
              label: 'Fichas de memorización'
            }].map((item, i) => <div key={i} className="card-premium p-5 flex items-center gap-3" data-id="element-65">
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center shrink-0" data-id="element-66">
                    <item.icon className="w-5 h-5 text-cyan-600" data-id="element-67" />
                  </div>
                  <p className="text-navy-900 font-semibold text-sm leading-tight" data-id="element-68">
                    {item.label}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* ───── QUÉ INCLUYE ───── */}
      <section id="incluye" className="py-16 md:py-24 bg-white" data-id="element-69">
        <div className="max-w-6xl mx-auto px-4" data-id="element-70">
          <SectionHeader eyebrow="Qué incluye" title="Todo lo que recibís hoy" subtitle="La guía principal más 6 bonos estratégicos. Todo en PDF, descarga inmediata." data-id="element-71" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-72">
            {bonuses.map((b, i) => <div key={i} className={`card-premium p-6 ${b.featured ? 'md:col-span-2 lg:col-span-3 border-cyan-500/30 shadow-[0_8px_30px_-10px_rgba(18,198,255,0.25)]' : ''}`} data-id="element-73">
                <div className="flex items-center justify-between gap-3 mb-4" data-id="element-74">
                  <div className="flex items-center gap-3" data-id="element-75">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-navy-900 to-navy-700 flex items-center justify-center shrink-0" data-id="element-76">
                      <b.icon className="w-5 h-5 text-cyan-400" data-id="element-77" />
                    </div>
                    <span className="text-[10px] font-bold text-cyan-700 uppercase tracking-widest bg-cyan-500/10 px-2.5 py-1 rounded-md border border-cyan-500/20" data-id="element-78">
                      {b.tag}
                    </span>
                  </div>
                  <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded" data-id="element-79">
                    USD {b.value}
                  </span>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-navy-900 mb-2 leading-tight" data-id="element-80">
                  {b.title}
                </h3>
                <p className="text-ink-500 leading-relaxed text-sm" data-id="element-81">{b.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* ───── BENEFICIOS ───── */}
      <section className="py-16 md:py-24 bg-mist cv-auto" data-id="element-82">
        <div className="max-w-5xl mx-auto px-4" data-id="element-83">
          <SectionHeader eyebrow="Beneficios" title="Lo que cambia para vos cuando estudiás con este sistema" data-id="element-84" />

          <div className="space-y-3 md:space-y-4" data-id="element-85">
            {benefits.map((item, i) => <div key={i} className="grid md:grid-cols-2 gap-3 md:gap-4" data-id="element-86">
                <div className="flex items-start gap-3 p-4 md:p-5 rounded-xl bg-white border border-navy-100" data-id="element-87">
                  <div className="w-6 h-6 rounded-full bg-red-50 border border-red-200 flex items-center justify-center shrink-0 mt-0.5" data-id="element-88">
                    <X className="w-3.5 h-3.5 text-red-500" strokeWidth={3} data-id="element-89" />
                  </div>
                  <div data-id="element-90">
                    <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider block mb-1" data-id="element-91">
                      Antes
                    </span>
                    <p className="text-ink-500 leading-snug text-sm md:text-base" data-id="element-92">
                      {item.before}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 md:p-5 rounded-xl bg-white border border-cyan-500/30 shadow-[0_4px_20px_-12px_rgba(18,198,255,0.4)]" data-id="element-93">
                  <div className="w-6 h-6 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0 mt-0.5" data-id="element-94">
                    <Check className="w-3.5 h-3.5 text-emerald-600" strokeWidth={3} data-id="element-95" />
                  </div>
                  <div data-id="element-96">
                    <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider block mb-1" data-id="element-97">
                      Después
                    </span>
                    <p className="text-navy-900 font-semibold leading-snug text-sm md:text-base" data-id="element-98">
                      {item.after}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* ───── IDEAL PARA ───── */}
      <section className="py-16 md:py-24 bg-white" data-id="element-99">
        <div className="max-w-4xl mx-auto px-4" data-id="element-100">
          <SectionHeader eyebrow="Ideal para vos" title="Este Mega Pack es para vos si…" data-id="element-101" />

          <div className="card-premium p-6 md:p-10" data-id="element-102">
            <ul className="space-y-4" data-id="element-103">
              {idealFor.map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-104">
                  <div className="w-7 h-7 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0 mt-0.5" data-id="element-105">
                    <Check className="w-4 h-4 text-cyan-600" strokeWidth={3} data-id="element-106" />
                  </div>
                  <span className="text-base md:text-lg text-ink-700 leading-relaxed" data-id="element-107">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* ───── TESTIMONIOS ───── */}
      <section className="py-16 md:py-24 bg-mist" data-id="element-108">
        <div className="max-w-6xl mx-auto px-4" data-id="element-109">
          <SectionHeader eyebrow="Confianza" title="Creado para estudiantes reales que necesitan resultados reales" subtitle="Más de 800 estudiantes de Latinoamérica ya cambiaron su forma de estudiar." data-id="element-110" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-111">
            {testimonials.map((t, i) => <TestimonialCard key={i} {...t} data-id="element-112" />)}
          </div>

          <div className="flex flex-col items-center gap-3 mt-10" data-id="element-113">
            <div className="flex items-center gap-1 text-amber-400" data-id="element-114">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-current" data-id="element-115" />)}
            </div>
            <p className="text-navy-900 font-bold" data-id="element-116">4.9/5 · +800 estudiantes</p>
          </div>
        </div>
      </section>

      {/* ───── VISTA PREVIA ───── */}
      <section className="py-16 md:py-24 bg-white" data-id="element-117">
        <div className="max-w-4xl mx-auto px-4" data-id="element-118">
          <SectionHeader eyebrow="Vista previa" title="Mirá cómo se ve por dentro" subtitle="Una muestra real de las páginas internas del Mega Pack." data-id="element-119" />

          <p className="text-center text-cyan-600 font-semibold text-sm mb-5" data-id="element-120">
            Página {currentSlide + 1} de {carouselSlides.length}
          </p>

          <div className="relative" data-id="element-121">
            <button onClick={prevSlide} aria-label="Anterior" className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 md:-translate-x-12 w-11 h-11 rounded-full bg-white border border-navy-100 flex items-center justify-center text-navy-900 hover:bg-navy-900 hover:text-white hover:border-navy-900 transition-colors z-20 shadow-premium" data-id="element-122">
              <ChevronLeft className="w-5 h-5" data-id="element-123" />
            </button>

            <button onClick={nextSlide} aria-label="Siguiente" className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 md:translate-x-12 w-11 h-11 rounded-full bg-white border border-navy-100 flex items-center justify-center text-navy-900 hover:bg-navy-900 hover:text-white hover:border-navy-900 transition-colors z-20 shadow-premium" data-id="element-124">
              <ChevronRight className="w-5 h-5" data-id="element-125" />
            </button>

            <div className="overflow-hidden rounded-2xl bg-white border border-navy-100 shadow-premium" data-id="element-126">
              <div className="flex transition-transform duration-500 ease-out" style={{
              transform: `translateX(-${currentSlide * 100}%)`
            }} data-id="element-127">
                {carouselSlides.map((slide, i) => {
                const distance = Math.abs(i - currentSlide);
                const isNear = distance <= 1 || distance >= carouselSlides.length - 1;
                return <div key={i} className="w-full shrink-0 relative" data-id="element-128">
                      {isNear ? <img src={slide.image} alt={slide.title} width={800} height={1200} loading={i === 0 ? 'eager' : 'lazy'} decoding="async" className="w-full h-auto object-contain bg-white block" data-id="element-129" /> : <div className="w-full bg-white" style={{
                    aspectRatio: '2 / 3'
                  }} aria-hidden="true" data-id="element-130" />}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-navy-950/95 via-navy-900/70 to-transparent p-5 pt-16" data-id="element-131">
                        <p className="text-xs uppercase tracking-widest text-cyan-400 font-bold mb-1" data-id="element-132">
                          {slide.subtitle}
                        </p>
                        <h3 className="text-base md:text-lg font-bold text-white leading-tight" data-id="element-133">
                          {slide.title}
                        </h3>
                      </div>
                    </div>;
              })}
              </div>
            </div>

            <div className="flex justify-center gap-2 mt-5" data-id="element-134">
              {carouselSlides.map((_, i) => <button key={i} onClick={() => setCurrentSlide(i)} aria-label={`Ir a página ${i + 1}`} className={`h-2 rounded-full transition-all duration-300 ${i === currentSlide ? 'bg-cyan-500 w-8' : 'bg-navy-100 hover:bg-navy-200 w-2'}`} data-id="element-135" />)}
            </div>
          </div>
        </div>
      </section>

      {/* ───── OFERTA ───── */}
      <section id="oferta" className="py-16 md:py-24 hero-bg relative overflow-hidden scroll-mt-4" data-id="element-136">
        <div className="absolute inset-0 hero-grid opacity-40 pointer-events-none" data-id="element-137"></div>
        <div className="relative max-w-2xl mx-auto px-4" data-id="element-138">
          {/* Countdown */}
          <div className="text-center mb-8" data-id="element-139">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600/95 shadow-[0_8px_24px_-8px_rgba(220,38,38,0.6)] mb-6" data-id="element-140">
              <Clock className="w-4 h-4 text-white" data-id="element-141" />
              <span className="text-white text-xs md:text-sm font-bold tracking-wider uppercase" data-id="element-142">
                Oferta termina en:
              </span>
            </div>

            <div className="flex items-center justify-center gap-2 md:gap-4 mb-5" data-id="element-143">
              {[{
              value: timeLeft.hours,
              label: 'hs'
            }, {
              value: timeLeft.minutes,
              label: 'min'
            }, {
              value: timeLeft.seconds,
              label: 'seg'
            }].map((unit, i) => <Fragment key={unit.label} data-id="element-144">
                  <div className="bg-navy-800/80 border border-navy-700/80 rounded-xl px-5 md:px-7 py-3 md:py-4 min-w-[80px] md:min-w-[100px] shadow-lg backdrop-blur-sm" data-id="element-145">
                    <div className="text-4xl md:text-5xl font-black text-white tracking-tight tabular-nums leading-none" data-id="element-146">
                      {String(unit.value).padStart(2, '0')}
                    </div>
                    <div className="text-[10px] md:text-xs text-navy-200 font-semibold uppercase tracking-wider mt-1" data-id="element-147">
                      {unit.label}
                    </div>
                  </div>
                  {i < 2 && <span className="text-red-500 text-2xl md:text-3xl font-black" data-id="element-148">
                      :
                    </span>}
                </Fragment>)}
            </div>

            <p className="flex items-center justify-center gap-2 text-amber-400 font-bold text-sm md:text-base" data-id="element-149">
              <AlertCircle className="w-4 h-4 shrink-0" data-id="element-150" />
              Después sube a su precio normal. No vuelve.
            </p>
          </div>

          {/* Price card with yellow border */}
          <div className="bg-navy-900/60 backdrop-blur-sm rounded-3xl border-2 border-amber-400/80 p-6 md:p-10 shadow-[0_30px_80px_-20px_rgba(251,191,36,0.25)] mb-6" data-id="element-151">
            {/* Red pill above price card */}
            <div className="flex justify-center -mt-12 md:-mt-14 mb-6" data-id="element-152">
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 shadow-[0_10px_30px_-8px_rgba(220,38,38,0.6)]" data-id="element-153">
                <Sparkles className="w-4 h-4 text-white" data-id="element-154" />
                <span className="text-white text-xs md:text-sm font-black tracking-wider uppercase" data-id="element-155">
                  Oferta por tiempo limitado
                </span>
              </div>
            </div>

            <p className="text-center text-navy-100 text-sm md:text-base leading-relaxed mb-6" data-id="element-156">
              <Check className="w-4 h-4 text-emerald-400 inline mr-1.5 -mt-0.5" strokeWidth={3} data-id="element-157" />
              Estás a un paso de acceder al{' '}
              <span className="text-amber-400 font-bold" data-id="element-158">
                Mega Pack · Anatomía Humana
              </span>
              , la guía ilustrada diseñada para que estudiantes de salud dominen
              los 12 sistemas del cuerpo humano.
            </p>

            {/* Value breakdown — compact */}
            <div className="bg-navy-950/50 rounded-2xl p-5 mb-6 border border-navy-700/50" data-id="element-159">
              <p className="text-[11px] font-bold text-cyan-400 uppercase tracking-widest mb-3 text-center" data-id="element-160">
                Todo lo que recibís
              </p>
              <ul className="space-y-2 text-sm" data-id="element-161">
                {['Guía Principal · 192 páginas ilustradas', 'Bono #1 · Mapa Maestro de Anatomía', 'Bono #2 · Guía Express de Términos', 'Bono #3 · Checklist de Estudio', 'Bono #4 · Plan de 14 Días', 'Bono #5 · Autoevaluación · 291 preguntas', 'Bono #6 · Fichas de Memorización'].map((item, i) => <li key={i} className="flex items-start gap-2 text-navy-100" data-id="element-162">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" strokeWidth={3} data-id="element-163" />
                    <span data-id="element-164">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* Price block */}
            <div className="text-center" data-id="element-165">
              <p className="text-amber-400 text-base md:text-lg font-bold line-through decoration-amber-400 decoration-2 mb-3" data-id="element-166">
                ¡Antes USD $99!
              </p>

              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-600 mb-4 shadow-md" data-id="element-167">
                <span className="text-white text-[11px] md:text-xs font-black tracking-wider uppercase" data-id="element-168">
                  Solo por hoy
                </span>
              </div>

              <div className="flex items-baseline justify-center gap-2 mb-3" data-id="element-169">
                <span className="text-xl md:text-2xl font-bold text-white" data-id="element-170">
                  USD
                </span>
                <span className="text-6xl md:text-8xl font-black text-amber-400 tracking-tight leading-none drop-shadow-[0_4px_12px_rgba(251,191,36,0.4)]" data-id="element-171">
                  $9.90
                </span>
              </div>

              <p className="flex items-center justify-center gap-1.5 text-navy-100 text-sm font-medium" data-id="element-172">
                <Globe className="w-4 h-4 text-cyan-400" data-id="element-173" />
                ¡Pagalo en tu moneda local!
              </p>
            </div>
          </div>

          {/* Yellow CTA button */}
          <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="block w-full text-center bg-gradient-to-b from-amber-300 to-amber-400 hover:from-amber-200 hover:to-amber-300 border-[3px] border-red-500 rounded-2xl px-6 py-5 md:py-6 text-navy-950 font-black text-base md:text-xl uppercase tracking-wider shadow-[0_15px_40px_-10px_rgba(251,191,36,0.6)] hover:shadow-[0_20px_50px_-10px_rgba(251,191,36,0.75)] transition-all hover:-translate-y-1 no-underline" data-id="element-174">
            👉 Sí, quiero la oferta ahora
          </a>

          {/* Trust line */}
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-6 text-xs md:text-sm text-navy-100 font-medium" data-id="element-175">
            <span className="flex items-center gap-1.5" data-id="element-176">
              <Lock className="w-4 h-4 text-amber-400" data-id="element-177" />
              Pago 100% seguro
            </span>
            <span className="text-navy-400" data-id="element-178">•</span>
            <span className="flex items-center gap-1.5" data-id="element-179">
              <ShieldCheck className="w-4 h-4 text-amber-400" data-id="element-180" />
              Garantía 7 días
            </span>
          </div>
          <p className="text-center text-navy-200 text-xs md:text-sm mt-3 flex items-center justify-center gap-1.5" data-id="element-181">
            <Globe className="w-3.5 h-3.5 text-cyan-400" data-id="element-182" />
            Pagalo en tu moneda local — Tarjeta de crédito, débito y más
          </p>
        </div>
      </section>

      {/* ───── GARANTÍA ───── */}
      <section className="py-16 md:py-20 bg-white cv-auto" data-id="element-183">
        <div className="max-w-3xl mx-auto px-4" data-id="element-184">
          <div className="card-premium p-6 md:p-10 flex flex-col md:flex-row items-center gap-6 md:gap-8 text-center md:text-left border-emerald-500/30" data-id="element-185">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-emerald-50 flex items-center justify-center shrink-0 border-2 border-emerald-200" data-id="element-186">
              <ShieldCheck className="w-10 h-10 md:w-12 md:h-12 text-emerald-600" data-id="element-187" />
            </div>
            <div data-id="element-188">
              <p className="text-emerald-600 font-bold text-xs uppercase tracking-widest mb-2" data-id="element-189">
                Garantía sin riesgo
              </p>
              <h3 className="text-xl md:text-2xl font-bold text-navy-900 mb-3" data-id="element-190">
                Probalo sin riesgo durante 7 días
              </h3>
              <p className="text-ink-500 leading-relaxed text-sm md:text-base" data-id="element-191">
                Si dentro de 7 días sentís que no era para vos, pedís devolución
                y te reembolsamos el 100%.{' '}
                <span className="text-navy-900 font-semibold" data-id="element-192">
                  Sin preguntas. Sin trámites complicados.
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───── COMMENTS ───── */}
      <div className="cv-auto" data-id="element-193">
        <Suspense fallback={<div className="h-[600px] bg-mist" data-id="element-195" />} data-id="element-194">
          <CommentsSection data-id="element-196" />
        </Suspense>
      </div>

      {/* ───── FAQ ───── */}
      <section className="py-16 md:py-24 bg-white cv-auto" data-id="element-197">
        <div className="max-w-3xl mx-auto px-4" data-id="element-198">
          <SectionHeader eyebrow="FAQ" title="Preguntas frecuentes" data-id="element-199" />

          <div className="space-y-3" data-id="element-200">
            {faqs.map((faq, i) => <div key={i} className="bg-white rounded-xl border border-navy-100 overflow-hidden transition-all duration-300" data-id="element-201">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-5 md:px-6 py-4 md:py-5 flex items-center justify-between text-left focus:outline-none hover:bg-mist transition-colors" data-id="element-202">
                  <span className="text-base md:text-lg font-semibold text-navy-900 pr-4" data-id="element-203">
                    {faq.q}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-cyan-600 shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-204" />
                </button>
                <div className={`px-5 md:px-6 overflow-hidden transition-all duration-300 ease-in-out ${openFaq === i ? 'max-h-96 pb-5 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-205">
                  <p className="text-ink-500 leading-relaxed text-sm md:text-base border-t border-navy-100 pt-4" data-id="element-206">
                    {faq.a}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* ───── FINAL CTA ───── */}
      <section className="py-16 md:py-24 hero-bg relative overflow-hidden cv-auto" data-id="element-207">
        <div className="absolute inset-0 hero-grid opacity-40 pointer-events-none" data-id="element-208"></div>
        <div className="relative max-w-3xl mx-auto px-4 text-center" data-id="element-209">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-6" data-id="element-210">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" data-id="element-211" />
            <p className="text-[11px] md:text-xs font-bold tracking-wider text-white uppercase" data-id="element-212">
              Última oportunidad — Precio bloqueado
            </p>
          </div>

          <h2 className="text-3xl md:text-5xl font-bold text-white leading-[1.05] mb-5" data-id="element-213">
            Dejá de estudiar anatomía{' '}
            <span className="cyan-gradient-text" data-id="element-214">sin un sistema</span>
          </h2>

          <p className="text-base md:text-lg text-navy-100 leading-relaxed mb-8 max-w-xl mx-auto" data-id="element-215">
            Accedé hoy al Mega Pack · Anatomía Humana y empezá a estudiar con
            claridad, orden y confianza.
          </p>

          <div className="flex items-baseline justify-center gap-1 mb-7" data-id="element-216">
            <span className="text-2xl md:text-3xl font-bold text-white" data-id="element-217">
              USD
            </span>
            <span className="text-5xl md:text-7xl font-black text-white tracking-tight" data-id="element-218">
              9.90
            </span>
          </div>

          <div className="max-w-xl mx-auto mb-5" data-id="element-219">
            <PrimaryCTA href={CHECKOUT_URL} full data-id="element-220">
              Quiero mi Mega Pack ahora
            </PrimaryCTA>
          </div>

          <p className="text-xs md:text-sm text-navy-100 font-medium" data-id="element-221">
            Descarga inmediata · 6 bonos estratégicos · Garantía 7 días
          </p>
        </div>
      </section>

      {/* ───── FOOTER ───── */}
      <footer className="bg-navy-950 border-t border-navy-800 py-10 px-4" data-id="element-222">
        <div className="max-w-3xl mx-auto text-center space-y-5" data-id="element-223">
          <div className="flex items-center justify-center gap-2 text-white font-bold text-base" data-id="element-224">
            <Stethoscope className="w-5 h-5 text-cyan-400" data-id="element-225" />
            Mega Pack · Anatomía Humana
          </div>
          <p className="text-navy-200 text-sm leading-relaxed max-w-xl mx-auto" data-id="element-226">
            La mejor forma de estudiar anatomía sin improvisar ni perder tiempo.
            Herramientas prácticas para estudiantes que quieren resultados
            concretos.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-navy-200" data-id="element-227">
            <a href="#" className="hover:text-cyan-400 transition-colors" data-id="element-228">
              Política de privacidad
            </a>
            <a href="#" className="hover:text-cyan-400 transition-colors" data-id="element-229">
              Términos y condiciones
            </a>
            <a href="#" className="hover:text-cyan-400 transition-colors" data-id="element-230">
              Política de cookies
            </a>
          </div>
          <p className="text-navy-300 text-xs pt-3" data-id="element-231">
            © 2026 Mega Pack · Anatomía Humana — Todos los derechos reservados.
            Producto digital · PDF de descarga inmediata.
          </p>
          <p className="text-navy-400 text-[10px] max-w-2xl mx-auto leading-relaxed" data-id="element-232">
            Los resultados descritos son ejemplos reales pero no están
            garantizados. Los resultados individuales pueden variar. Este
            producto no reemplaza la enseñanza académica formal.
          </p>
        </div>
      </footer>

      <Suspense fallback={null} data-id="element-233">
        <SocialProofPopup data-id="element-234" />
      </Suspense>
    </div>;
}